// src/components/league-dashboard/PositionAssignmentDropdown.js - Smart Position Assignment

import React, { useState, useMemo, useRef } from 'react';
import { needsActivateWhenFull, eligibleSlotMates } from '../../services/activateWhenFull';
import { formatApiError } from '../../services/errorUtils';
import {
  ChevronDown, AlertTriangle, CheckCircle, Users,
  Crown, Shield, Heart, Activity, X, ArrowLeftRight
} from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import PlayerLink from '../PlayerLink';
import { getEffectiveEligibility } from '../../utils/positionEligibility';
import { isPitcherPosition } from '../../utils/playerClassification';

const PositionAssignmentDropdown = ({
  player,
  league,
  currentRoster,
  onAssign,
  onCancel,
  isVisible = false,
  mode = 'add', // 'add' for adding from free agents, 'move' for moving existing roster players
  currentStatus = null, // Current roster status if mode === 'move'
  currentPosition = null, // Current position slot if mode === 'move'
  // Phase 2.2: drop-shuffle entry point. Parent owns RosterPlanModal
  // state; this callback fires when user clicks the 5th "Drop a player"
  // button. The button only renders when (active full AND bench/DL/
  // minors has room). Per ROSTER_PLAN_SPEC.md § B.5.1.
  onOpenDropShuffle = null
}) => {
  const [selectedStatus, setSelectedStatus] = useState(currentStatus || 'active');
  const [selectedOption, setSelectedOption] = useState('');
  const [error, setError] = useState('');
  const [showAlternatives, setShowAlternatives] = useState(false);
  // User-intent ref (Bug 1 fix, 2026-05-15 — same as mobile twin). Flips
  // to true on any explicit status-button click or position-row click.
  // The auto-select useEffect below early-returns when this is true, so
  // clicking ACTIVE with no available active slots doesn't silently revert
  // to bench. Reset on modal close so the next open auto-selects fresh.
  const userPickedSlotRef = useRef(false);
  // DD-DL-DEADLOCK-0726 — activate-when-full. Only for a DL player with no open eligible slot.
  const [displaceChoice, setDisplaceChoice] = useState(null);

  // Reset status when modal opens in move mode with current status
  React.useEffect(() => {
    if (isVisible && mode === 'move' && currentStatus) {
      setSelectedStatus(currentStatus);
    }
  }, [isVisible, mode, currentStatus]);

  // ========================================
  // POSITION AVAILABILITY ANALYSIS
  // ========================================
  const analyzePositionAvailability = useMemo(() => {
    // Skip expensive analysis if modal is not visible
    if (!isVisible) {
      return {
        available: [],
        alternatives: [],
        benchOption: null,
        minorsOption: null
      };
    }

    if (!league?.roster_settings?.position_requirements || !Array.isArray(currentRoster)) {
      return {
        available: [],
        alternatives: [],
        benchOption: null,
        minorsOption: null
      };
    }

    // Canonical eligibility ONLY (Bible §4.5.17 Layer 1, Rule 9): the shared
    // helper returns null when the canonical field is missing — no silent
    // fallback to primary-position-only (that skips composite-slot expansion
    // and auto-earned carry-over). Missing data → no eligible slots, surfaced.
    const eligiblePositions = getEffectiveEligibility(player) || [];
    
    const positionRequirements = league.roster_settings.position_requirements;
    const benchSlots = league.roster_settings.bench_slots || 0;
    const dlSlots = league.roster_settings.dl_slots || 0;
    const minorSlots = league.roster_settings.minor_league_slots || 0;

    // Count current active assignments AND track which specific slots are occupied
    // NOTE: currentRoster uses canonical structure ONLY - league_context.roster.status and league_context.roster.position
    const activeAssignments = {};
    const occupiedSlots = new Set(); // Track EXACT slot IDs that are occupied (e.g., "C_0", "C_1")
    
    currentRoster.forEach(rosterPlayer => {
      const status = rosterPlayer.league_context?.roster?.status;
      const position = rosterPlayer.league_context?.roster?.position;
      if (status === 'active' && position) {
        const positionBase = position.split('_')[0]; // Extract position from "C_0" format
        activeAssignments[positionBase] = (activeAssignments[positionBase] || 0) + 1;
        occupiedSlots.add(position); // Track the EXACT slot (e.g., "C_1")
      }
    });

    // Count bench/DL/minors usage (canonical structure ONLY)
    const benchCount = currentRoster.filter(p => 
      p.league_context?.roster?.status === 'bench'
    ).length;
    const dlCount = currentRoster.filter(p => 
      p.league_context?.roster?.status === 'dl'
    ).length;
    const minorsCount = currentRoster.filter(p => 
      p.league_context?.roster?.status === 'minors'
    ).length;

    // Helper function to check position eligibility with special cases
    const isEligibleForPosition = (pos) => {
      if (eligiblePositions.includes(pos)) return true;
      
      // Special position logic
      if (pos === 'MI' && (eligiblePositions.includes('2B') || eligiblePositions.includes('SS'))) return true;
      if (pos === 'CI' && (eligiblePositions.includes('1B') || eligiblePositions.includes('3B'))) return true;
      if (pos === 'UTIL' && !eligiblePositions.some(isPitcherPosition)) return true;
      if (pos === 'P' && ['SP', 'RP'].some(p => eligiblePositions.includes(p))) return true;
      if (pos === 'OF' && ['LF', 'CF', 'RF', 'OF'].some(p => eligiblePositions.includes(p))) return true;
      
      return false;
    };

    // Analyze each position type
    const available = [];
    const alternatives = [];

    Object.entries(positionRequirements).forEach(([position, config]) => {
      const maxSlots = config?.slots || 0;
      const currentlyFilled = activeAssignments[position] || 0;
      const availableSlots = maxSlots - currentlyFilled;
      
      if (isEligibleForPosition(position)) {
        // 🔧 FIX: Find the FIRST VACANT slot, not just use the count
        let vacantSlotId = null;
        if (availableSlots > 0) {
          // Check each slot index from 0 to maxSlots-1
          for (let i = 0; i < maxSlots; i++) {
            const potentialSlotId = `${position}_${i}`;
            if (!occupiedSlots.has(potentialSlotId)) {
              vacantSlotId = potentialSlotId;
              break; // Found the first vacant slot!
            }
          }
        }
        
        const option = {
          position,
          availableSlots,
          maxSlots,
          currentlyFilled,
          canAssign: availableSlots > 0,
          slotId: vacantSlotId // ✅ Use the FIRST VACANT slot
        };
        
        if (availableSlots > 0) {
          available.push(option);
        } else {
          alternatives.push(option);
        }
      }
    });

    // Bench option (if enabled and has space)
    const benchOption = benchSlots > 0 ? {
      position: 'bench',
      availableSlots: benchSlots - benchCount,
      maxSlots: benchSlots,
      currentlyFilled: benchCount,
      canAssign: benchCount < benchSlots,
      slotId: 'bench'
    } : null;

    // Minors option (if enabled and has space)
    const minorsOption = minorSlots > 0 ? {
      position: 'minors',
      availableSlots: minorSlots - minorsCount,
      maxSlots: minorSlots,
      currentlyFilled: minorsCount,
      canAssign: minorsCount < minorSlots,
      slotId: 'minors'
    } : null;

    return {
      available,
      alternatives,
      benchOption,
      minorsOption,
      activeAssignments
    };
  }, [isVisible, league, currentRoster, player]);

  // ========================================
  // AUTO-SELECT BEST POSITION ON MODAL OPEN
  // ========================================
  React.useEffect(() => {
    // Bug 1 fix (2026-05-15): early-return when the user has explicitly
    // picked anything in this modal session. Without this guard, clicking
    // ACTIVE (which clears selectedOption) re-fires the auto-select and
    // the bench fallback silently overwrites the user's intent. The ref
    // resets on modal close so the next open auto-selects fresh.
    if (isVisible && player && !selectedOption && !userPickedSlotRef.current) {
      const { available, benchOption, minorsOption } = analyzePositionAvailability;

      // Priority order: direct position match > utility positions > bench > minors
      let bestOption = null;

      // Find direct position match first (canonical structure ONLY)
      const playerPosition = player?.info?.position;
      const directMatch = available.find(opt => opt.position === playerPosition);

      if (directMatch) {
        bestOption = directMatch.slotId;
        setSelectedStatus('active');
      } else if (available.length > 0) {
        // Use first available position (sorted by priority in analysis)
        bestOption = available[0].slotId;
        setSelectedStatus('active');
      } else if (benchOption?.canAssign) {
        // No active slots — auto-switch to bench status
        bestOption = benchOption.slotId;
        setSelectedStatus('bench');
      } else if (minorsOption?.canAssign) {
        bestOption = minorsOption.slotId;
        setSelectedStatus('minors');
      }

      if (bestOption) {
        setSelectedOption(bestOption);
      }
    }

    // Reset selection when modal closes
    if (!isVisible) {
      setSelectedOption('');
      setError('');
      setShowAlternatives(false);
      // Bug 1 fix: reset user-intent so next open re-runs auto-select fresh.
      userPickedSlotRef.current = false;
    }
  }, [isVisible, player, analyzePositionAvailability, selectedOption]);

  // ========================================
  // EVENT HANDLERS
  // ========================================
  // DD-DL-DEADLOCK-0726 — ACTIVATE-WHEN-FULL (Bible §4.5.15.1).
  // A DL-locked team at capacity cannot free a slot with a standalone move: that move names only
  // a NON-flagged player and the gate refuses it. Two owners hit this in production. Offer the
  // teammates whose slots this player can actually take, and send the pair as ONE move.
  // Deliberately narrow: bench placement is a FAILSAFE in this league, not roster management —
  // needsActivateWhenFull is false unless the player is DL and no eligible slot is open.
  const flaggedPosition = player?.league_context?.position || player?.info?.position;
  const positionRequirements = league?.roster_settings?.position_requirements;
  // CANONICAL eligibility (Bible §4.5.17 Layer 1) — the same helper this component already uses
  // for slot availability. A bare position string would miss composite-slot expansion and
  // auto-earned eligibility, so a player who has earned OF would be offered the wrong teammates.
  const flaggedEligibility = getEffectiveEligibility(player) || null;
  const showDisplaceChooser = (
    mode === 'move' && currentStatus === 'dl' && selectedStatus === 'active'
    && needsActivateWhenFull(currentRoster, flaggedPosition, positionRequirements, flaggedEligibility)
  );
  const slotMates = showDisplaceChooser
    ? eligibleSlotMates(currentRoster, flaggedPosition, flaggedEligibility)
    : [];

  const handleAssignment = async () => {
    if (!selectedOption) {
      setError('Please select a position assignment option');
      return;
    }

    setError('');

    // Compute proper roster_position for non-active statuses.
    // Bench/minors need slot IDs (bench_0, minors_0, etc.) so DraftRosterPanel
    // can map them to the correct slots.
    const { benchOption, minorsOption } = analyzePositionAvailability;
    let rosterPosition;
    if (selectedStatus === 'active') {
      rosterPosition = selectedOption;
    } else if (selectedStatus === 'bench') {
      rosterPosition = `bench_${benchOption?.currentlyFilled || 0}`;
    } else if (selectedStatus === 'minors') {
      rosterPosition = `minors_${minorsOption?.currentlyFilled || 0}`;
    } else {
      rosterPosition = null;
    }

    if (showDisplaceChooser && !displaceChoice) {
      setError('Your active roster is full. Choose a teammate to move to the bench.');
      return;
    }

    try {
      await onAssign({
        player,
        assignment: selectedOption,
        roster_status: selectedStatus,
        // when displacing, the activation lands in the slot the chosen teammate vacates
        roster_position: displaceChoice ? displaceChoice.slot : rosterPosition,
        displace_league_player_id: displaceChoice ? displaceChoice.leaguePlayerId : undefined,
        mode: mode
      });
    } catch (err) {
      // buildActivatePlanSteps / the server both THROW rather than emit an invalid plan —
      // render the reason instead of failing silently.
      setError(formatApiError(err, 'Failed to assign position'));
    }
  };

  const resetSelection = () => {
    setSelectedOption('');
    setError('');
    setShowAlternatives(false);
  };

  // ========================================
  // OPTION RENDERING
  // ========================================
  const renderPositionOption = (option, isAlternative = false) => {
    const { position, availableSlots, maxSlots, currentlyFilled, canAssign, slotId } = option;
    
    const getPositionIcon = (pos) => {
      if (pos === 'bench') return Users;
      if (pos === 'minors') return Shield;
      if (pos === 'dl') return Heart;
      return Activity;
    };
    
    const Icon = getPositionIcon(position);
    const isSelected = selectedOption === slotId;
    
    return (
      <div
        key={slotId || position}
        onClick={() => {
          if (canAssign) {
            userPickedSlotRef.current = true;
            setSelectedOption(slotId);
          }
        }}
        className={`
          p-3 rounded-lg border-2 cursor-pointer transition-all duration-200
          ${isSelected 
            ? `${dynastyTheme.classes.border.primaryBright} bg-yellow-400/10` 
            : canAssign 
              ? `${dynastyTheme.classes.border.neutral} hover:border-yellow-400/50 hover:bg-yellow-400/5`
              : `${dynastyTheme.classes.border.neutral} opacity-50 cursor-not-allowed bg-neutral-800/20`
          }
          ${isAlternative ? 'border-amber-500/30 bg-amber-500/5' : ''}
        `}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Icon className={`w-4 h-4 ${
              canAssign 
                ? dynastyTheme.classes.text.primary 
                : dynastyTheme.classes.text.neutralDark
            }`} />
            <div>
              <span className={`font-medium text-sm ${
                canAssign 
                  ? dynastyTheme.classes.text.white 
                  : dynastyTheme.classes.text.neutralDark
              }`}>
                {position === 'bench' ? 'Bench' :
                 position === 'minors' ? 'Minor Leagues' :
                 position.toUpperCase()}
              </span>
              {position === 'minors' && (
                <div className={`text-xs ${dynastyTheme.classes.text.warning} mt-1`}>
                  No contract until called up
                </div>
              )}
            </div>
          </div>
          
          <div className="text-right">
            <div className={`text-xs ${
              canAssign 
                ? dynastyTheme.classes.text.neutralLight 
                : dynastyTheme.classes.text.neutralDark
            }`}>
              {currentlyFilled}/{maxSlots} slots
            </div>
            {canAssign ? (
              <div className={`text-xs ${dynastyTheme.classes.text.success} font-medium`}>
                {availableSlots} available
              </div>
            ) : (
              <div className={`text-xs ${dynastyTheme.classes.text.error} font-medium`}>
                Full
              </div>
            )}
          </div>
        </div>
        
        {isSelected && (
          <div className={`mt-2 pt-2 border-t border-yellow-400/30`}>
            <div className="flex items-center justify-between">
              <div className={`text-xs ${dynastyTheme.classes.text.primary} flex items-center gap-1`}>
                <CheckCircle className="w-3 h-3" />
                Selected
              </div>
              {!isAlternative && (
                <span className={`text-xs px-2 py-0.5 rounded ${dynastyTheme.classes.bg.success} ${dynastyTheme.classes.text.black} font-medium`}>
                  Recommended
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ========================================
  // ELIGIBILITY CHECK DISPLAY
  // ========================================
  const renderEligibilityInfo = () => {
    // Canonical eligibility ONLY (Rule 9) — shared helper, no primary-position fallback.
    const eligiblePositions = getEffectiveEligibility(player) || [];
    
    return (
      <div className={`p-3 ${dynastyTheme.classes.bg.darkLighter} rounded-lg mb-4`}>
        <div className="flex items-center gap-2 mb-2">
          <Crown className={`w-4 h-4 ${dynastyTheme.classes.text.primary}`} />
          <PlayerLink mlbId={player?.ids?.mlb} name={`${player?.info?.first_name || ''} ${player?.info?.last_name || ''}`.trim()} style={{ fontWeight: 500, fontSize: '0.875rem', color: '#f9fafb' }} />
          <span className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
            {player?.info?.mlb_team}
          </span>
        </div>
        <div className="flex flex-wrap gap-1">
          <span className={`text-xs ${dynastyTheme.classes.text.neutralLight} mr-2`}>Eligible:</span>
          {eligiblePositions.map(pos => (
            <span 
              key={pos}
              className={`text-xs px-2 py-1 ${dynastyTheme.classes.bg.primary} ${dynastyTheme.classes.text.black} rounded font-medium`}
            >
              {pos}
            </span>
          ))}
        </div>
      </div>
    );
  };

  // ========================================
  // MAIN COMPONENT RENDER
  // ========================================
  if (!isVisible) return null;

  const { available, alternatives, benchOption, minorsOption } = analyzePositionAvailability;
  const hasAvailableOptions = available.length > 0 || 
                            (benchOption && benchOption.canAssign) || 
                            (minorsOption && minorsOption.canAssign);

  // DD-FIXES-0705 Fix 2 fail-loud guard (Rule 9): a missing/malformed league
  // shape is a CALLER BUG and presents as one — never as a roster condition.
  // Same treatment as MobilePositionAssignmentModal; callers pass the
  // canonical §5 league object (useAssignmentLeagueConfig). No reshaping.
  if (!league?.roster_settings?.position_requirements) {
    console.error(
      'PositionAssignmentDropdown: league prop is missing roster_settings.position_requirements —',
      'caller must pass the canonical league shape (Bible §5).',
      league
    );
    return (
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
        style={{ zIndex: 1000000 }}
      >
        <div className={`${dynastyTheme.components.card.base} w-full max-w-md`}>
          <div className="p-6">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className={`w-5 h-5 ${dynastyTheme.classes.text.error}`} />
              <span className={`font-medium ${dynastyTheme.classes.text.error}`}>League configuration unavailable</span>
            </div>
            <p className={`${dynastyTheme.classes.text.neutralLight} text-sm mb-4`}>
              Position availability can't be computed because the league's roster
              configuration didn't load. This is an app error — not a full roster.
              Close and try again; if it persists, report it.
            </p>
            <button onClick={onCancel} className={`${dynastyTheme.components.button.secondary} w-full`}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      style={{ zIndex: 1000000 }}
    >
      <div className={`${dynastyTheme.components.card.base} w-full max-w-md max-h-[90vh] overflow-y-auto`}>
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className={`${dynastyTheme.components.heading.h3} mb-0`}>
              Assign Roster Position
            </h2>
            <button
              onClick={onCancel}
              className={`p-2 rounded-lg ${dynastyTheme.classes.bg.darkLighter} hover:bg-neutral-600/50 ${dynastyTheme.classes.transition}`}
            >
              <X className={`w-4 h-4 ${dynastyTheme.classes.text.neutralLight}`} />
            </button>
          </div>

          {/* Player Eligibility Info */}
          {renderEligibilityInfo()}

          {/* Roster Status Selection (for move mode or always show) */}
          <div className="mb-6">
            <h3 className={`text-sm font-medium ${dynastyTheme.classes.text.white} mb-3`}>
              Roster Status
            </h3>
            {/*
              Grid sizes to fit the 5th "Drop a player" button when it
              renders (active full + bench/DL/minors has room). Per
              ROSTER_PLAN_SPEC.md § B.5.1 patched condition.
            */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              <button
                onClick={() => {
                  userPickedSlotRef.current = true;
                  setSelectedStatus('active');
                  setSelectedOption('');
                }}
                className={`
                  p-3 rounded-lg border-2 transition-all duration-200
                  ${selectedStatus === 'active'
                    ? `${dynastyTheme.classes.border.primaryBright} ${dynastyTheme.classes.bg.primary}/10`
                    : `${dynastyTheme.classes.border.neutral} hover:border-yellow-400/50`
                  }
                `}
              >
                <div className="flex flex-col items-center gap-2">
                  <Activity className={`w-5 h-5 ${selectedStatus === 'active' ? dynastyTheme.classes.text.primary : dynastyTheme.classes.text.neutralLight}`} />
                  <span className={`text-xs font-medium ${selectedStatus === 'active' ? dynastyTheme.classes.text.white : dynastyTheme.classes.text.neutralLight}`}>
                    Active
                  </span>
                </div>
              </button>

              {benchOption && benchOption.maxSlots > 0 && (
                <button
                  onClick={() => {
                    userPickedSlotRef.current = true;
                    setSelectedStatus('bench');
                    setSelectedOption('bench');
                  }}
                  className={`
                    p-3 rounded-lg border-2 transition-all duration-200
                    ${selectedStatus === 'bench'
                      ? `${dynastyTheme.classes.border.primaryBright} ${dynastyTheme.classes.bg.primary}/10`
                      : `${dynastyTheme.classes.border.neutral} hover:border-yellow-400/50`
                    }
                  `}
                >
                  <div className="flex flex-col items-center gap-2">
                    <Users className={`w-5 h-5 ${selectedStatus === 'bench' ? dynastyTheme.classes.text.primary : dynastyTheme.classes.text.neutralLight}`} />
                    <span className={`text-xs font-medium ${selectedStatus === 'bench' ? dynastyTheme.classes.text.white : dynastyTheme.classes.text.neutralLight}`}>
                      Bench
                    </span>
                  </div>
                </button>
              )}

              {league?.roster_settings?.dl_slots > 0 && (
                <button
                  onClick={() => {
                    userPickedSlotRef.current = true;
                    setSelectedStatus('dl');
                    setSelectedOption('dl');
                  }}
                  className={`
                    p-3 rounded-lg border-2 transition-all duration-200
                    ${selectedStatus === 'dl'
                      ? `${dynastyTheme.classes.border.primaryBright} ${dynastyTheme.classes.bg.primary}/10`
                      : `${dynastyTheme.classes.border.neutral} hover:border-yellow-400/50`
                    }
                  `}
                >
                  <div className="flex flex-col items-center gap-2">
                    <Heart className={`w-5 h-5 ${selectedStatus === 'dl' ? dynastyTheme.classes.text.primary : dynastyTheme.classes.text.neutralLight}`} />
                    <span className={`text-xs font-medium ${selectedStatus === 'dl' ? dynastyTheme.classes.text.white : dynastyTheme.classes.text.neutralLight}`}>
                      DL
                    </span>
                  </div>
                </button>
              )}

              {minorsOption && minorsOption.maxSlots > 0 && (
                <button
                  onClick={() => {
                    userPickedSlotRef.current = true;
                    setSelectedStatus('minors');
                    setSelectedOption('minors');
                  }}
                  className={`
                    p-3 rounded-lg border-2 transition-all duration-200
                    ${selectedStatus === 'minors'
                      ? `${dynastyTheme.classes.border.primaryBright} ${dynastyTheme.classes.bg.primary}/10`
                      : `${dynastyTheme.classes.border.neutral} hover:border-yellow-400/50`
                    }
                  `}
                >
                  <div className="flex flex-col items-center gap-2">
                    <Shield className={`w-5 h-5 ${selectedStatus === 'minors' ? dynastyTheme.classes.text.primary : dynastyTheme.classes.text.neutralLight}`} />
                    <span className={`text-xs font-medium ${selectedStatus === 'minors' ? dynastyTheme.classes.text.white : dynastyTheme.classes.text.neutralLight}`}>
                      Minors
                    </span>
                  </div>
                </button>
              )}

              {/*
                5th button — Drop a player. Per ROSTER_PLAN_SPEC.md
                § B.5.1. Renders only when:
                  - active slots are full (available.length === 0), AND
                  - bench/DL/minors has room (hasAvailableOptions), AND
                  - parent provided onOpenDropShuffle callback.
                Parent owns the modal lifecycle; clicking this fires
                the parent callback (close this modal, open RosterPlanModal).
                Visual treatment: action-style (ArrowLeftRight icon) to
                differentiate from the four status destinations.
              */}
              {available.length === 0 && hasAvailableOptions && onOpenDropShuffle && (
                <button
                  onClick={onOpenDropShuffle}
                  title="Drop a roster player to make room for this player"
                  className={`
                    p-3 rounded-lg border-2 transition-all duration-200
                    ${dynastyTheme.classes.border.neutral} hover:border-yellow-400/50 hover:bg-yellow-400/5
                  `}
                >
                  <div className="flex flex-col items-center gap-2">
                    <ArrowLeftRight className={`w-5 h-5 ${dynastyTheme.classes.text.primary}`} />
                    <span className={`text-xs font-medium ${dynastyTheme.classes.text.white}`}>
                      Drop a player
                    </span>
                  </div>
                </button>
              )}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className={`${dynastyTheme.components.badge.error} p-3 rounded-lg mb-4`}>
              <div className="flex items-center gap-2">
                <AlertTriangle className={`w-4 h-4 ${dynastyTheme.classes.text.error}`} />
                <span className={dynastyTheme.classes.text.error}>{error}</span>
              </div>
            </div>
          )}

          {/* No Available Options */}
          {!hasAvailableOptions && (
            <div className={`${dynastyTheme.components.badge.warning} p-4 rounded-lg mb-4`}>
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className={`w-5 h-5 ${dynastyTheme.classes.text.warning}`} />
                <span className={`font-medium ${dynastyTheme.classes.text.warning}`}>All Positions Full</span>
              </div>
              <p className={`text-sm ${dynastyTheme.classes.text.warning} mb-3`}>
                All eligible positions are currently filled. Consider:
              </p>
              <div className="space-y-2 text-sm">
                <div>• Dropping another player to make room</div>
                <div>• Trading players to create space</div>
                <div>• Checking if bench slots are available</div>
              </div>
            </div>
          )}

          {/* Active-only-full panel — Bug 1 fix (2026-05-15, mobile parity).
              When the user is on the ACTIVE tab and every active slot is
              filled but bench/DL/minors has room, surface a focused
              "Drop a Player to Make Room" CTA so they can route through
              the canonical drop-shuffle plan flow (Bible § 4.5.10) without
              hunting for the small 5th button in the status grid. */}
          {selectedStatus === 'active' && available.length === 0 && hasAvailableOptions && (
            <div className={`${dynastyTheme.components.badge.warning} p-4 rounded-lg mb-4`}>
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className={`w-5 h-5 ${dynastyTheme.classes.text.warning}`} />
                <span className={`font-medium ${dynastyTheme.classes.text.warning}`}>All Active Positions Full</span>
              </div>
              <p className={`text-sm ${dynastyTheme.classes.text.warning} mb-3`}>
                All eligible active positions are currently filled. Drop a roster player to make room, or pick bench/DL/minors above.
              </p>
              {onOpenDropShuffle && (
                <button
                  onClick={onOpenDropShuffle}
                  className={`w-full ${dynastyTheme.classes.bg.primary} ${dynastyTheme.classes.text.black} px-4 py-2 rounded-lg font-semibold hover:opacity-90 transition-colors flex items-center justify-center gap-2`}
                >
                  <ArrowLeftRight className="w-4 h-4" />
                  Drop a Player to Make Room
                </button>
              )}
            </div>
          )}

          {/* Available Position Options (only show if status is 'active') */}
          {selectedStatus === 'active' && available.length > 0 && (
            <div className="mb-6">
              <h3 className={`text-sm font-medium ${dynastyTheme.classes.text.white} mb-3 flex items-center gap-2`}>
                <CheckCircle className={`w-4 h-4 ${dynastyTheme.classes.text.success}`} />
                Available Active Lineup Positions
              </h3>
              <div className="space-y-2">
                {available.map(option => renderPositionOption(option))}
              </div>
            </div>
          )}

          {/* Show Alternatives Button (only for active status) */}
          {selectedStatus === 'active' && alternatives.length > 0 && !showAlternatives && (
            <button
              onClick={() => setShowAlternatives(true)}
              className={`w-full p-3 mb-4 ${dynastyTheme.classes.bg.darkLighter} rounded-lg border-2 border-amber-500/30 hover:bg-amber-500/10 ${dynastyTheme.classes.transition}`}
            >
              <div className="flex items-center justify-center gap-2">
                <AlertTriangle className={`w-4 h-4 ${dynastyTheme.classes.text.warning}`} />
                <span className={`${dynastyTheme.classes.text.warning} font-medium`}>
                  View Full Positions ({alternatives.length} currently full)
                </span>
                <ChevronDown className={`w-4 h-4 ${dynastyTheme.classes.text.warning}`} />
              </div>
            </button>
          )}

          {/* Full Positions (Alternatives) - only for active status */}
          {selectedStatus === 'active' && showAlternatives && alternatives.length > 0 && (
            <div className="mb-6">
              <h3 className={`text-sm font-medium ${dynastyTheme.classes.text.warning} mb-3 flex items-center gap-2`}>
                <AlertTriangle className={`w-4 h-4 ${dynastyTheme.classes.text.warning}`} />
                Currently Full Positions
                <button
                  onClick={() => setShowAlternatives(false)}
                  className={`ml-auto text-xs ${dynastyTheme.classes.text.neutralLight} hover:text-white`}
                >
                  Hide
                </button>
              </h3>
              <div className="space-y-2">
                {alternatives.map(option => renderPositionOption(option, true))}
              </div>
              <div className={`mt-3 p-3 ${dynastyTheme.classes.bg.darkLighter} rounded-lg`}>
                <p className={`text-xs ${dynastyTheme.classes.text.warning}`}>
                  These positions are full. You would need to move another player first.
                </p>
              </div>
            </div>
          )}

          {/* DD-DL-DEADLOCK-0726 — ACTIVATE-WHEN-FULL chooser (Bible §4.5.15.1).
              Shown only for a DL player when no eligible slot is open. The list is the occupants
              of slots THIS player can occupy — the owner who hit this in production was trying to
              bench an outfielder to make room for a first baseman, which could never have worked. */}
          {showDisplaceChooser && (
            <div data-testid="displace-chooser" className="mb-4">
              <h3 className={`text-sm font-semibold ${dynastyTheme.classes.text.white} mb-1`}>
                Your active roster is full
              </h3>
              <p className={`text-xs ${dynastyTheme.classes.text.neutralLight} mb-3`}>
                Choose a teammate to move to the bench. {player?.info?.first_name}{' '}
                {player?.info?.last_name} will take his slot — both happen as one move, and
                nobody is dropped.
              </p>
              {slotMates.length === 0 ? (
                <p className={`text-xs ${dynastyTheme.classes.text.warning}`}>
                  No teammate holds a slot this player is eligible for.
                </p>
              ) : (
                <div className="space-y-2">
                  {slotMates.map((m) => (
                    <button
                      key={m.leaguePlayerId}
                      type="button"
                      data-testid={`displace-option-${m.slot}`}
                      onClick={() => {
                        // Choosing a slot-mate IS the slot selection: with a full active roster
                        // there is no vacant option to pick, so without this the Assign button
                        // stays disabled and the chooser is unsubmittable. (Caught by render test.)
                        userPickedSlotRef.current = true;
                        setDisplaceChoice(m);
                        setSelectedOption(m.slot);
                        setError('');
                      }}
                      className={`w-full text-left p-2 rounded-lg ${dynastyTheme.classes.transition} ${
                        displaceChoice?.leaguePlayerId === m.leaguePlayerId
                          ? `${dynastyTheme.classes.bg.primary} ${dynastyTheme.classes.text.black}`
                          : `${dynastyTheme.classes.bg.neutral}/20 ${dynastyTheme.classes.text.white}`
                      }`}
                    >
                      <span className="font-medium">{m.name}</span>
                      <span className="text-xs ml-2 opacity-75">{m.slot}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              className={dynastyTheme.utils.getComponent('button', 'secondary', 'md')}
            >
              Cancel
            </button>
            <button
              onClick={handleAssignment}
              disabled={!selectedOption || !hasAvailableOptions}
              className={`
                ${!selectedOption || !hasAvailableOptions 
                  ? 'px-5 py-2.5 text-base bg-neutral-700/50 text-neutral-500 cursor-not-allowed rounded-md' 
                  : dynastyTheme.utils.getComponent('button', 'primary', 'md')
                }
                flex-1
              `}
            >
              {selectedOption ? 'Assign Player' : 'Select Position'}
            </button>
          </div>

          {/* Helpful Tips */}
          <div className={`mt-6 p-4 ${dynastyTheme.classes.bg.darkLighter} rounded-lg`}>
            <h4 className={`text-sm font-medium ${dynastyTheme.classes.text.white} mb-2`}>Tips:</h4>
            <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} space-y-1`}>
              <div>• Active lineup players count against salary cap</div>
              <div>• Bench players count against salary cap</div>
              <div>• Minor league players have no contract (no salary)</div>
              {league?.roster_settings?.dl_slots > 0 && <div>• DL players count against salary cap but don't play</div>}
              <div>• You can move players between positions later</div>
            </div>
          </div>

          {/* Debug Info (Development Only) */}
          {process.env.NODE_ENV === 'development' && (
            <div className={`mt-4 p-3 ${dynastyTheme.classes.bg.darkLighter} rounded-lg`}>
              <h4 className={`text-xs font-bold ${dynastyTheme.classes.text.primary} mb-2`}>Debug Info:</h4>
              <pre className={`text-xs ${dynastyTheme.classes.text.neutralLight} overflow-auto`}>
                {JSON.stringify({
                  effectiveEligibility: player?.league_context?.roster?.effective_eligibility,
                  defaultEligibility: player?.league_context?.roster?.default_eligibility,
                  customEligibility: player?.league_context?.roster?.custom_eligibility,
                  leaguePositions: league?.roster_settings?.position_requirements,
                  benchSlots: league?.roster_settings?.bench_slots,
                  dlSlots: league?.roster_settings?.dl_slots,
                  minorSlots: league?.roster_settings?.minor_league_slots,
                  activeAssignments: analyzePositionAvailability.activeAssignments,
                  selectedOption
                }, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PositionAssignmentDropdown;